import * as React from 'react';
import * as styles from "./OuterPanel.css";
import { MessagesStore } from '../../messages/stores';
import { inject, observer, Provider } from 'mobx-react';
import { IAPanel } from '../../panels/Panel';
import { IAChoiceGroup } from '../../choiceGroup/ChoiceGroup';
import { Helper } from '../../../Helper';
import { IADateAndTimePicker } from '../../dateAndTimePicker/DateAndTimePicker';
import { IAIcon } from '../../icon/Icon';
import { LocalizationService } from '../../../services/LocalizationService';
import { IATreeview } from '../../treeview/Treeview';
import { ITreeViewItem } from '../../../interfaces/ITreeViewItem';
import { RIGHTARROW_KEY_CODE } from '../../../global/Constants';
import "../../../styles/BasicIA.css";
import InstanceService from '../../../services/InstanceService';
import { IInstance } from '../../../interfaces/IInstance';

export interface IOuterFilterPanelProps {
  showOuterPanel?: boolean;
  instancesList?: any[],
  messagesStore?: MessagesStore;
  clearFiltersKey?: string;
  disableESCFunction?: boolean;
  tenant: string;
  token: string;
  environment: "Development" | "Test" | "Production";
  reloadMessages: (resetPageCount) => void;
  close: () => void;
}

export interface IOuterFilterPanelState {
  showOuterPanel: boolean;
  statusExpand: boolean;
  typesExpand: boolean;
  dateExpand: boolean;
  instancesExpand: boolean;
  availableInstances: any[];
  typesOfContent: any[];
  itemStatus: any[];
}

@inject('messagesStore')
@observer
export class OuterFilterPanel extends React.Component<IOuterFilterPanelProps, IOuterFilterPanelState> {

  private readonly localizationService: LocalizationService = new LocalizationService();
  private focusRef: React.RefObject<any>;

  constructor(props: IOuterFilterPanelProps) {
    super(props);

    this.state = {
      statusExpand: false,
      typesExpand: false,
      dateExpand: true,
      instancesExpand: true,
      availableInstances: undefined,
      typesOfContent: undefined,
      itemStatus: undefined,
      showOuterPanel: props.showOuterPanel,
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());


    this.focusRef = React.createRef();
  }

  public componentWillReceiveProps(props: IOuterFilterPanelProps): void {
    if (this.props.clearFiltersKey !== props.clearFiltersKey) {
      this.loadFilter();
    }
    if (this.props.showOuterPanel !== props.showOuterPanel) {
      this.setState({ showOuterPanel: props.showOuterPanel });
    }
    if (this.props.instancesList !== props.instancesList) {
      this.props.messagesStore.availableInstances = props.instancesList;
      this.setState({ availableInstances: props.instancesList });
      this.loadFilter();
    }
  }

  public componentWillUnmount(): void {
    document.removeEventListener("keydown", (event) => this.keyListener(event), false);
  }

  public componentDidMount(): void {
    document.addEventListener("keydown", (event) => this.keyListener(event), false);
  }

  private keyListener(event: KeyboardEvent): void {
    if (event.keyCode === RIGHTARROW_KEY_CODE && this.props.showOuterPanel) {
      this.focusRef.current.focus();
    }
  }

  private loadTypesOfContent(): void {
    const typesOfContent: ITreeViewItem[] = [
      { id: "IntraActive-Messages", title: "Message", checked: false, children: undefined },
      { id: "IntraActive-Events", title: "Events", checked: false, children: undefined },
      { id: "IntraActive-ContentOrganizer", title: "Pages", checked: false, children: undefined },
    ];
    this.setState({ typesOfContent: typesOfContent });
  }

  private loadStatus(): void {
    const itemStatus: ITreeViewItem[] = [
      { id: "Draft", title: this.localizationService.strings.ContentCreator_Draft, checked: false, children: undefined },
      { id: "Published", title: this.localizationService.strings.ContentCreator_Published, checked: false, children: undefined },
      { id: "Scheduled", title: this.localizationService.strings.ContentCreator_Scheduled, checked: false, children: undefined },
      { id: "Expired", title: this.localizationService.strings.ContentCreator_Expired, checked: false, children: undefined },
      { id: "Pending", title: this.localizationService.strings.ContentCreator_Pending, checked: false, children: undefined },
      { id: "Recurrence", title: this.localizationService.strings.Events_Recurrence, checked: false, children: undefined },
    ];
    this.setState({ itemStatus: itemStatus });
  }

  private loadFilter(): void {

    let availableInstances = Array.from(this.props.messagesStore.availableInstances);
    let readableMessageInstances = this.props.messagesStore.readableMessageInstances !== undefined ? Array.from(this.props.messagesStore.readableMessageInstances) : undefined;
    let readableEventsInstances = this.props.messagesStore.readableEventsInstances !== undefined ? Array.from(this.props.messagesStore.readableEventsInstances) : undefined;
    availableInstances = availableInstances.length > 0 && availableInstances[0]["key"] !== undefined ? availableInstances : availableInstances.map(item => ({ key: item }));

    if (availableInstances && readableMessageInstances && readableEventsInstances) {

      this.setFilterOptions(availableInstances, readableMessageInstances, readableEventsInstances);
    } else if (availableInstances && (!readableMessageInstances || !readableEventsInstances)) {

      const fetchMessagesInstanceList = InstanceService.getInstances(this.props.environment, this.props.token, this.props.tenant, "IntraActive-Messages", false);
      const fetchEventsInstanceList = InstanceService.getInstances(this.props.environment, this.props.token, this.props.tenant, "IntraActive-Events", false);

      Promise.all([fetchMessagesInstanceList, fetchEventsInstanceList]).then((response: IInstance[][]) => {
        const messagesInstanceList = response[0];
        const eventsInstanceList = response[1];

        messagesInstanceList.forEach(messageInstance => {
          if (!messageInstance.instanceName) {
            messageInstance.instanceName = "Default";
          }
        });
        eventsInstanceList.forEach(eventInstance => {
          if (!eventInstance.instanceName) {
            eventInstance.instanceName = "Default";
          }
        });

        this.props.messagesStore.readableMessageInstances = messagesInstanceList;
        this.props.messagesStore.readableEventsInstances = eventsInstanceList;

        this.setFilterOptions(availableInstances, messagesInstanceList, eventsInstanceList);
      });
    }
  }

  private setFilterOptions(availableInstances: any[], readableMessageInstances: any[], readableEventsInstances: any[]) {
    availableInstances.forEach(element => {
      if (element.key.toLowerCase().includes("intraactive-messages")) {
        const instanceName: string = element.key.slice(23);
        element.instanceName = instanceName;
        element.type = "IntraActive-Messages";
        element.guid = `${element.type}-${instanceName}`;
      } else if (element.key.toLowerCase().includes("intraactive-events")) {
        const instanceName: string = element.key.slice(21);
        element.instanceName = instanceName;
        element.type = "IntraActive-Events";
        element.guid = `${element.type}-${instanceName}`;
      } else if (element.key.toLowerCase().includes("intraactive-contentorganizer")) {
        const instanceName: string = element.key.slice(31);
        element.instanceName = instanceName;
        element.type = "IntraActive-ContentOrganizer";
        element.guid = `${element.type}-${instanceName}`;
      }
    });

    let availableMessageInstances = availableInstances.filter(x => x.type === "IntraActive-Messages");
    let availableEventsInstances = availableInstances.filter(x => x.type === "IntraActive-Events");
    let availableContentOrganizerInstances = availableInstances.filter(x => x.type === "IntraActive-ContentOrganizer");

    readableMessageInstances.forEach(readEl => {
      availableMessageInstances.forEach(avaiEl => {
        if (readEl.instanceName === avaiEl.instanceName || (!readEl.instanceName && avaiEl.instanceName === "Default")) {
          avaiEl.title = readEl.title.length > 0 ? readEl.title : readEl.instanceName ? readEl.instanceName : "Default";
          if (!readEl.instanceName) {
            avaiEl.instanceName = "IAMessages";
          }
        }
      });
    });

    availableMessageInstances.forEach(el => {
      if (el["title"] === undefined) {
        el.title = el.instanceName;
      }
    });

    readableEventsInstances.forEach(readEl => {
      availableEventsInstances.forEach(avaiEl => {
        if (readEl.instanceName === avaiEl.instanceName) {
          avaiEl.title = readEl.title.length > 0 ? readEl.title : readEl.instanceName;
        }
      });
    });

    availableEventsInstances.forEach(el => {
      if (el["title"] === undefined) {
        el.title = el.instanceName;
      }
    });

    availableContentOrganizerInstances.forEach(el => {
      if (el["title"] === undefined) {
        el.title = el.instanceName;
      }
    });
    const typesOfContent: ITreeViewItem[] = [];

    typesOfContent.push({ id: "IntraActive-Messages", title: "Message", checked: false, children: availableMessageInstances });
    typesOfContent.push({ id: "IntraActive-Events", title: "Events", checked: false, children: availableEventsInstances });
    typesOfContent.push({ id: "IntraActive-ContentOrganizer", title: "Pages", checked: false, children: availableContentOrganizerInstances });

    // const readableInstances: any[] = availableMessageInstances.concat(availableEventsInstances).concat(availableContentOrganizerInstances);
    // const unique = new Set();
    // const uniqueReadableInstances = readableInstances.filter(el => {
    //   const duplicate = unique.has(el.guid);
    //   unique.add(el.guid);
    //   return !duplicate;
    // });

    // this.props.messagesStore.availableInstances = typesOfContent;
    this.setState({
      availableInstances: typesOfContent
    });
    this.loadTypesOfContent();
    this.loadStatus();
  }

  private hasSelectedChild(item: ITreeViewItem): void {
    let hasSelectedChild = false;
    item.children?.forEach((child: ITreeViewItem) => {
      if (child.checked) {
        hasSelectedChild = true;
        this.props.messagesStore.filterData.selectedInstances.push(
          {
            id: child.id,
            name: child.instanceName,
            guid: child.guid
          }
        );
      } else {
        this.props.messagesStore.filterData.selectedInstances = this.props.messagesStore.filterData.selectedInstances.filter(x => x.guid !== child.guid);
      }
    });
    if (hasSelectedChild || item.checked) {
      if (this.props.messagesStore.filterData.selectedTypesOfContent.filter(x => x.id === item.id).length === 0) {
        this.props.messagesStore.filterData.selectedTypesOfContent.push({
          id: item.id,
          title: item.name
        });
      }
    } else {
      this.props.messagesStore.filterData.selectedTypesOfContent = this.props.messagesStore.filterData.selectedTypesOfContent.filter(x => x.id !== item.id);
    }
  }

  public render(): JSX.Element {

    let content = (
      <div className={styles.IA_contentCreatorFilter}>

        <div className={styles.IA_filterTitleHeadline}>
          {new LocalizationService().strings.ContentCreator_FilterPanel_Headline}
        </div>

        <div className={'Intances'}>
          <div
            ref={this.focusRef}
            className="IA_OnFocusOutline_outer"
            tabIndex={0}
            onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === "Enter") {
                this.setState({ instancesExpand: !this.state.instancesExpand });
              }
            }}
            onClick={() => {
              this.setState({ instancesExpand: !this.state.instancesExpand });
            }}
          >
            <div
              className={[styles.IA_expandHeader, "IA_OnFocusOutline_inner"].join(" ")}
              tabIndex={-1}
            >
              <div
                className={styles.IA_expandButton}
                style={{
                  color: this.props.messagesStore.highlightColor
                }}
              >
                <IAIcon
                  url={this.state.instancesExpand ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg" : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg"}
                  size={18}
                  color={"#767676"}
                />
              </div>
              <div className={styles.IA_filterTitle}>
                {new LocalizationService().strings.ContentCreator_FilterPanel_Instances}
              </div>
            </div>
          </div>
          {this.state.instancesExpand &&
            <div className={styles.IA_viewPanelContentWrapper}>
              {this.props.messagesStore.availableInstances &&
                <IATreeview
                  highlightColor={this.props.messagesStore.highlightColor}
                  actionType="Checkbox"
                  expanded={false}
                  structureType="Arrow"
                  onChange={(items: ITreeViewItem[]) => {
                    this.setState({ availableInstances: items });
                    this.props.messagesStore.filterData.selectedInstances = [];
                    this.props.messagesStore.filterData.selectedTypesOfContent = [];
                    items.forEach((item: ITreeViewItem) => {
                      if (item.checked) {
                        this.props.messagesStore.filterData.selectedTypesOfContent.push({
                          id: item.id,
                          title: item.name
                        });
                      } else {
                        this.props.messagesStore.filterData.selectedTypesOfContent = this.props.messagesStore.filterData.selectedTypesOfContent.filter(x => x.id !== item.id);
                      }
                      this.hasSelectedChild(item);
                    });
                    setTimeout(() => {
                      this.props.messagesStore.filterData.isFiltered = true;
                      this.props.messagesStore.filterData.hasChanged = true;
                      this.props.messagesStore.updateFilter(this.props.messagesStore.filterData);
                      this.props.reloadMessages(true);
                    }, 1000);
                  }}
                  items={this.state.availableInstances}
                />
              }
            </div>
          }
        </div>

        <div className={styles.IA_filterPanelSpace} />

        <div className={"Date"}>
          <div
            className="IA_OnFocusOutline_outer"
            tabIndex={0}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                this.setState({ dateExpand: !this.state.dateExpand });
              }
            }}
            onClick={() => {
              this.setState({ dateExpand: !this.state.dateExpand });
            }}
          >
            <div
              className={[styles.IA_expandHeader, "IA_OnFocusOutline_inner"].join(" ")}
              tabIndex={-1}
            >
              <div
                className={styles.IA_expandButton}
                style={{
                  color: this.props.messagesStore.highlightColor
                }}
              >
                <IAIcon
                  url={this.state.dateExpand ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg" : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg"}
                  size={18}
                  color={"#767676"}
                />
              </div>
              <div className={styles.IA_filterTitle}>
                {new LocalizationService().strings.ContentCreator_FilterPanel_Date}

              </div>
            </div>
          </div>
          {this.state.dateExpand &&
            <div className={styles.IA_viewPanelContentWrapper}>
              <IAChoiceGroup
                className={styles.IA_defaultChoiceGroup}
                selectedKey={this.props.messagesStore.filterData.showPeriodMessage}
                highlightColor={this.props.messagesStore.highlightColor}
                options={[
                  {
                    key: 'All time',
                    text: new LocalizationService().strings.ContentCreator_FilterPanel_AllTime,
                    disabled: false
                  },
                  {
                    key: 'Period',
                    text: new LocalizationService().strings.ContentCreator_FilterPanel_Period,
                    disabled: false
                  }
                ]}
                required={true}
                onChange={(key) => {
                  let filterData = this.props.messagesStore.filterData;
                  filterData.showPeriodMessage = key;
                  filterData.isFiltered = true;
                  filterData.hasChanged = true;
                  this.props.messagesStore.updateFilter(filterData);
                  this.props.reloadMessages(true);
                }}
                style={{
                  marginTop: 10
                }}
              />
              <IADateAndTimePicker
                label={new LocalizationService().strings.Messages_FilterPanel_StartTime}
                selectedDate={this.props.messagesStore.filterData.startDate}
                showTimeSelector={true}
                highlightColor={this.props.messagesStore.highlightColor}
                isClearable={true}
                disabled={this.props.messagesStore.filterData.showPeriodMessage === "All time"}
                onChange={(startDate: Date) => {
                  let filterData = this.props.messagesStore.filterData;
                  filterData.startDate = startDate;
                  filterData.isFiltered = true;
                  filterData.hasChanged = true;
                  filterData.showPeriodMessage = 'Period';
                  this.props.messagesStore.updateFilter(filterData);
                  this.props.reloadMessages(true);
                }}
                style={{
                  marginTop: 0,
                  width: 250,
                  // opacity: 0.5
                }}
              />
              <IADateAndTimePicker
                label={new LocalizationService().strings.ContentCreator_FilterPanel_EndTime}
                selectedDate={this.props.messagesStore.filterData.endDate}
                showTimeSelector={true}
                highlightColor={this.props.messagesStore.highlightColor}
                isClearable={true}
                disabled={this.props.messagesStore.filterData.showPeriodMessage === "All time"}
                onChange={(endDate: Date) => {
                  let filterData = this.props.messagesStore.filterData;
                  filterData.endDate = endDate;
                  filterData.isFiltered = true;
                  filterData.hasChanged = true;
                  filterData.showPeriodMessage = 'Period';
                  this.props.messagesStore.updateFilter(filterData);
                  this.props.reloadMessages(true);
                }}
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  width: 250,
                  // opacity: 0.5
                }}
              />
            </div>
          }
        </div>
        <div className={styles.IA_filterPanelSpace} />

        <div className={"Status"}>
          <div
            className="IA_OnFocusOutline_outer"
            tabIndex={0}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                this.setState({ statusExpand: !this.state.statusExpand });
              }
            }}
            onClick={() => {
              this.setState({ statusExpand: !this.state.statusExpand });
            }}
          >
            <div
              className={[styles.IA_expandHeader, "IA_OnFocusOutline_inner"].join(" ")}
              tabIndex={-1}
            >
              <div
                className={styles.IA_expandButton}
                style={{
                  color: this.props.messagesStore.highlightColor
                }}
              >
                <IAIcon
                  url={this.state.statusExpand ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg" : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg"}
                  size={18}
                  color={"#767676"}
                />
              </div>
              <div className={styles.IA_filterTitle}>
                {new LocalizationService().strings.ContentCreator_FilterPanel_ItemStatus}
              </div>
            </div>
          </div>
          {this.state.statusExpand &&
            <div className={styles.IA_viewPanelContentWrapper}>
              <IATreeview
                highlightColor={this.props.messagesStore.highlightColor}
                actionType="Checkbox"
                expanded={false}
                structureType="Arrow"
                onChange={(items: ITreeViewItem[]) => {
                  this.setState({ itemStatus: items });
                  this.props.messagesStore.filterData.selectedItemStatus = [];
                  items.forEach((item: ITreeViewItem) => {
                    if (item.checked) {
                      this.props.messagesStore.filterData.selectedItemStatus.push({
                        item
                      })
                    }
                  });
                  setTimeout(() => {
                    this.props.messagesStore.filterData.isFiltered = true;
                    this.props.messagesStore.filterData.hasChanged = true;
                    this.props.messagesStore.updateFilter(this.props.messagesStore.filterData);
                    this.props.reloadMessages(true);
                  }, 100);
                }}
                items={this.state.itemStatus}
              />
            </div>
          }
        </div>

        <div className={styles.IA_filterPanelSpace} />

      </div>
    );
    let top = 0;
    if (Helper.getDisplayEnvironment() === "Sharepoint" || Helper.getDisplayEnvironment() === "SharepointClassic") {
      top = 48;
    }
    if (this.props.messagesStore.isMobileApp) {
      top = window.platform === "android" ? 50 : Helper.isIPhoneXType() ? 90 : 70;
    }
    return (
      <Provider messagesStore={this.props.messagesStore}>
        <IAPanel
          isOuterPanel={true}
          showNavigationBar={true}
          open={this.state.showOuterPanel}
          transition={this.props.messagesStore.panelIsOpen ? "fade" : "slide"}
          width={this.props.messagesStore.isMobile ? window.innerWidth : 360}
          isMobile={this.props.messagesStore.isMobile}
          highlightColor={this.props.messagesStore.highlightColor}
          marginTop={top}
          disableESCFunction={this.props.disableESCFunction}
          close={() => {
            this.props.messagesStore.hideOuterPanel();
            this.props.close();
          }}
          onClearFilters={() => {
            this.props.messagesStore.updateFilter({
              isFiltered: false,
              showOnlyAuthorMessages: "All",
              showPeriodMessage: "All time",
              selectedPeolple: [],
              selectedItemStatus: [],
              selectedCategories: [],
              startDate: undefined,
              endDate: undefined,
              selectedTypesOfContent: [],
              selectedInstances: [],
              hasChanged: true,
            });

            this.loadFilter();
            this.props.reloadMessages(true);
          }}
        >{content}</IAPanel>
      </Provider>

    );
  }
}